<script setup>
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import {
  generateTranslationPayload,
  generateLogActionKey,
} from 'dashboard/helper/auditlogHelper';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import subDays from 'date-fns/subDays';
import { getUnixStartOfDay, getUnixEndOfDay } from 'shared/helpers/DateHelper';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const records = computed(() => getters['auditlogs/getAuditLogs'].value);
const uiFlags = computed(() => getters['auditlogs/getUIFlags'].value);
const meta = computed(() => getters['auditlogs/getMeta'].value);
const agentList = computed(() => getters['agents/getAgents'].value);

const { t } = useI18n();
const route = useRoute();

const routerPage = computed(() => Number(route.query.page ?? 1));
const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedAgent = ref(null);

const filterSince = computed(() => getUnixStartOfDay(customDateRange.value[0]));
const filterUntil = computed(() => getUnixEndOfDay(customDateRange.value[1]));

const fetchAuditLogs = (page = routerPage.value, opts = {}) => {
  const since = opts.since ?? filterSince.value;
  const until = opts.until ?? filterUntil.value;
  const user_id =
    opts.user_id !== undefined ? opts.user_id : selectedAgent.value?.id;
  try {
    store.dispatch('auditlogs/fetch', {
      page,
      since,
      until,
      user_id: user_id || undefined,
    });
  } catch (error) {
    const errorMessage = error?.message || t('AUDIT_LOGS.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const applyFilters = () => {
  router.push({
    name: 'auditlogs_list',
    query: {
      page: 1,
      since: String(filterSince.value),
      until: String(filterUntil.value),
      ...(selectedAgent.value?.id
        ? { user_id: String(selectedAgent.value.id) }
        : {}),
    },
  });
};

const onDateRangeChange = value => {
  if (value && value.length === 2) {
    customDateRange.value = value;
    applyFilters();
  }
};

const onAgentChange = () => {
  applyFilters();
};

const initFiltersFromRoute = () => {
  const { since, until, user_id } = route.query;
  if (since && until) {
    customDateRange.value = [
      new Date(Number(since) * 1000),
      new Date(Number(until) * 1000),
    ];
  }
  if (user_id && agentList.value?.length) {
    const id = Number(user_id);
    selectedAgent.value = agentList.value.find(a => a.id === id) ?? null;
  } else {
    selectedAgent.value = null;
  }
};

const generateLogText = auditLogItem => {
  const payload = generateTranslationPayload(auditLogItem, agentList.value);
  const translationKey = generateLogActionKey(auditLogItem);

  const joinIfArray = value => {
    return Array.isArray(value) ? value.join(', ') : value;
  };

  const mergedPayload = {
    ...payload,
    attributes: joinIfArray(payload.attributes),
    values: joinIfArray(payload.values),
  };
  return t(translationKey, mergedPayload);
};

const onPageChange = page => {
  router.push({
    name: 'auditlogs_list',
    query: {
      page: String(page),
      since: String(filterSince.value),
      until: String(filterUntil.value),
      ...(selectedAgent.value?.id
        ? { user_id: String(selectedAgent.value.id) }
        : {}),
    },
  });
};

onMounted(() => {
  store.dispatch('agents/get');
  initFiltersFromRoute();
  fetchAuditLogs();
});

watch(
  () => [
    route.query.page,
    route.query.since,
    route.query.until,
    route.query.user_id,
  ],
  () => {
    initFiltersFromRoute();
    fetchAuditLogs();
  }
);

watch(agentList, () => {
  if (route.query.user_id && agentList.value?.length) {
    const id = Number(route.query.user_id);
    selectedAgent.value = agentList.value.find(a => a.id === id) ?? null;
  }
});

const tableHeaders = computed(() => {
  return [
    t('AUDIT_LOGS.LIST.TABLE_HEADER.ACTIVITY'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.TIME'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.IP_ADDRESS'),
  ];
});

// Helper to get action type info for visual styling
const getActionTypeInfo = auditLogItem => {
  const action = auditLogItem.action?.toLowerCase();
  const auditableType = auditLogItem.auditable_type?.toLowerCase();

  // Sign in/out actions
  if (auditableType === 'user') {
    if (action === 'sign_in') {
      return {
        icon: 'i-lucide-log-in',
        bgColor: 'bg-n-teal-3',
        iconColor: 'text-n-teal-11',
      };
    }
    if (action === 'sign_out') {
      return {
        icon: 'i-lucide-log-out',
        bgColor: 'bg-n-slate-3',
        iconColor: 'text-n-slate-11',
      };
    }
  }

  // Create actions
  if (action === 'create') {
    return {
      icon: 'i-lucide-plus-circle',
      bgColor: 'bg-n-teal-3',
      iconColor: 'text-n-teal-11',
    };
  }

  // Update actions
  if (action === 'update') {
    return {
      icon: 'i-lucide-pencil',
      bgColor: 'bg-n-blue-3',
      iconColor: 'text-n-blue-11',
    };
  }

  // Delete/destroy actions
  if (action === 'destroy') {
    return {
      icon: 'i-lucide-trash-2',
      bgColor: 'bg-n-ruby-3',
      iconColor: 'text-n-ruby-11',
    };
  }

  // Default
  return {
    icon: 'i-lucide-activity',
    bgColor: 'bg-n-slate-3',
    iconColor: 'text-n-slate-11',
  };
};

// Helper to get auditable type badge
const getAuditableTypeBadge = auditLogItem => {
  const auditableType = auditLogItem.auditable_type?.toLowerCase();

  const typeMap = {
    automationrule: {
      label: 'Automation',
      bgColor: 'bg-n-iris-3',
      textColor: 'text-n-iris-11',
    },
    webhook: {
      label: 'Webhook',
      bgColor: 'bg-n-amber-3',
      textColor: 'text-n-amber-11',
    },
    inbox: {
      label: 'Inbox',
      bgColor: 'bg-n-blue-3',
      textColor: 'text-n-blue-11',
    },
    user: {
      label: 'User',
      bgColor: 'bg-n-teal-3',
      textColor: 'text-n-teal-11',
    },
    team: {
      label: 'Team',
      bgColor: 'bg-n-iris-3',
      textColor: 'text-n-iris-11',
    },
    macro: {
      label: 'Macro',
      bgColor: 'bg-n-amber-3',
      textColor: 'text-n-amber-11',
    },
    accountuser: {
      label: 'Account User',
      bgColor: 'bg-n-teal-3',
      textColor: 'text-n-teal-11',
    },
    inboxmember: {
      label: 'Inbox Member',
      bgColor: 'bg-n-blue-3',
      textColor: 'text-n-blue-11',
    },
    teammember: {
      label: 'Team Member',
      bgColor: 'bg-n-iris-3',
      textColor: 'text-n-iris-11',
    },
    account: {
      label: 'Account',
      bgColor: 'bg-n-slate-3',
      textColor: 'text-n-slate-11',
    },
    conversation: {
      label: 'Conversation',
      bgColor: 'bg-n-ruby-3',
      textColor: 'text-n-ruby-11',
    },
  };

  return (
    typeMap[auditableType] || {
      label: auditableType || 'Unknown',
      bgColor: 'bg-n-slate-3',
      textColor: 'text-n-slate-11',
    }
  );
};

// Format relative time for better UX
const formatRelativeTime = createdAt => {
  const now = new Date();
  const date = new Date(createdAt * 1000);
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800)
    return `${Math.floor(diffInSeconds / 86400)}d ago`;

  return messageTimestamp(createdAt, 'MMM dd, yyyy');
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="$t('AUDIT_LOGS.LOADING')"
    :no-records-found="!records.length && !uiFlags.fetchingList"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('AUDIT_LOGS.HEADER')"
        :description="$t('AUDIT_LOGS.DESCRIPTION')"
        :link-text="$t('AUDIT_LOGS.LEARN_MORE')"
        feature-name="audit_logs"
        icon-name="clipboard"
      />
    </template>

    <template #preBody>
      <div
        class="flex flex-row flex-nowrap items-center justify-between gap-4 rounded-xl border border-n-weak bg-n-alpha-1 px-4 py-3 mb-4 w-full"
      >
        <div class="flex flex-row items-center gap-3 flex-shrink-0">
          <span
            class="text-sm font-medium text-n-slate-11 whitespace-nowrap w-24"
          >
            {{ $t('AUDIT_LOGS.FILTERS.DATE_RANGE.LABEL') }}
          </span>
          <div class="min-w-[240px] flex-shrink-0">
            <WootDateRangePicker
              :value="customDateRange"
              :placeholder="$t('AUDIT_LOGS.FILTERS.DATE_RANGE.PLACEHOLDER')"
              :confirm-text="$t('AUDIT_LOGS.FILTERS.APPLY')"
              @change="onDateRangeChange"
            />
          </div>
        </div>
        <div class="flex flex-row items-center gap-3 flex-shrink-0">
          <span
            class="text-sm font-medium text-n-slate-11 whitespace-nowrap w-12"
          >
            {{ $t('AUDIT_LOGS.FILTERS.AGENT.LABEL') }}
          </span>
          <div class="multiselect-wrap--small w-[220px] flex-shrink-0">
            <multiselect
              v-model="selectedAgent"
              class="no-margin"
              track-by="id"
              label="name"
              :placeholder="$t('AUDIT_LOGS.FILTERS.AGENT.PLACEHOLDER')"
              :options="agentList || []"
              allow-empty
              :clear-on-select="false"
              selected-label=""
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              @update:model-value="onAgentChange"
            />
          </div>
        </div>
      </div>
    </template>

    <template v-if="!records.length && !uiFlags.fetchingList">
      <div
        class="flex flex-col items-center justify-center py-16 px-4 rounded-xl bg-n-alpha-1 border border-n-weak"
      >
        <div
          class="flex items-center justify-center w-16 h-16 rounded-full bg-n-slate-3 mb-4"
        >
          <Icon icon="i-lucide-scroll-text" class="size-8 text-n-slate-10" />
        </div>
        <h3 class="text-base font-medium text-n-slate-12 mb-1">
          {{ $t('AUDIT_LOGS.LIST.404') }}
        </h3>
        <p class="text-sm text-n-slate-11 text-center max-w-md">
          {{ $t('AUDIT_LOGS.DESCRIPTION') }}
        </p>
      </div>
    </template>

    <template #body>
      <div
        v-if="records.length"
        class="rounded-xl border border-n-weak overflow-hidden bg-n-alpha-1"
      >
        <table class="min-w-full">
          <thead class="bg-n-alpha-2">
            <tr>
              <th
                v-for="(thHeader, index) in tableHeaders"
                :key="thHeader"
                class="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider text-n-slate-11"
                :class="{
                  'w-[55%]': index === 0,
                  'w-[25%]': index === 1,
                  'w-[20%]': index === 2,
                }"
              >
                {{ thHeader }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr
              v-for="auditLogItem in records"
              :key="auditLogItem.id"
              class="group hover:bg-n-alpha-2 transition-colors duration-150"
            >
              <td class="py-4 px-4">
                <div class="flex items-start gap-3">
                  <div
                    class="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                    :class="getActionTypeInfo(auditLogItem).bgColor"
                  >
                    <Icon
                      :icon="getActionTypeInfo(auditLogItem).icon"
                      class="size-4"
                      :class="getActionTypeInfo(auditLogItem).iconColor"
                    />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p
                      class="text-sm font-medium text-n-slate-12 leading-snug break-words"
                    >
                      {{ generateLogText(auditLogItem) }}
                    </p>
                    <div class="flex items-center gap-2 mt-1.5">
                      <span
                        class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium"
                        :class="[
                          getAuditableTypeBadge(auditLogItem).bgColor,
                          getAuditableTypeBadge(auditLogItem).textColor,
                        ]"
                      >
                        {{ getAuditableTypeBadge(auditLogItem).label }}
                      </span>
                    </div>
                  </div>
                </div>
              </td>
              <td class="py-4 px-4">
                <div class="flex flex-col">
                  <span
                    class="text-sm font-medium text-n-slate-12"
                    :title="
                      messageTimestamp(
                        auditLogItem.created_at,
                        'MMM dd, yyyy hh:mm a'
                      )
                    "
                  >
                    {{ formatRelativeTime(auditLogItem.created_at) }}
                  </span>
                  <span class="text-xs text-n-slate-10 mt-0.5">
                    {{ messageTimestamp(auditLogItem.created_at, 'hh:mm a') }}
                  </span>
                </div>
              </td>
              <td class="py-4 px-4">
                <div class="flex items-center gap-2">
                  <Icon
                    icon="i-lucide-globe"
                    class="size-4 text-n-slate-9 flex-shrink-0"
                  />
                  <span
                    class="text-sm text-n-slate-11 font-mono"
                    :title="auditLogItem.remote_address"
                  >
                    {{ auditLogItem.remote_address || '—' }}
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <TableFooter
          :current-page="Number(meta.currentPage)"
          :total-count="meta.totalEntries"
          :page-size="meta.perPage"
          class="border-t border-n-weak bg-n-alpha-1"
          @page-change="onPageChange"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
